/**
 * =============================================================================
 * SPEQ55 RFP Pro - RFP Analysis Results Page
 * =============================================================================
 *
 * Displays the results of an RFP analysis including:
 * - Project Summary
 * - Technical Abstract
 * - Technical Parameters Table
 * - Follow-up Chat
 *
 * @module pages/rfps/RFPAnalysisResults
 * @author SPEQ55
 */

import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { get } from '../../api/client';
import {
  getAnalysis,
  downloadAnalysisPDF,
  downloadAnalysisCSV,
  downloadOutputSchema,
} from '../../api/analysisApi';
import AnalysisChat from '../../components/analysis/AnalysisChat';

// Poll interval for pending/processing analyses
const POLL_INTERVAL = 3000;

function RFPAnalysisResults() {
  const { id, analysisId } = useParams();

  // State
  const [rfp, setRfp] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('summary');

  // Sorting for parameters table
  const [sortField, setSortField] = useState('parameter');
  const [sortDir, setSortDir] = useState('asc');

  // PDF download state
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  // CSV download state
  const [downloadingCsv, setDownloadingCsv] = useState(false);

  // Schema download state
  const [downloadingSchema, setDownloadingSchema] = useState(false);

  /**
   * Handle CSV download.
   */
  const handleDownloadCsv = async () => {
    setDownloadingCsv(true);
    try {
      await downloadAnalysisCSV(id, analysisId, rfp?.rfpNumber);
    } catch {
      alert('Failed to download CSV. Please try again.');
    } finally {
      setDownloadingCsv(false);
    }
  };

  /**
   * Handle output schema download.
   */
  const handleDownloadSchema = async () => {
    setDownloadingSchema(true);
    try {
      await downloadOutputSchema();
    } catch {
      alert('Failed to download schema. Please try again.');
    } finally {
      setDownloadingSchema(false);
    }
  };

  /**
   * Handle PDF download.
   */
  const handleDownloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      await downloadAnalysisPDF(id, analysisId, rfp?.rfpNumber);
    } catch {
      alert('Failed to download PDF. Please try again.');
    } finally {
      setDownloadingPdf(false);
    }
  };

  /**
   * Fetch analysis data.
   */
  const fetchAnalysis = useCallback(async () => {
    try {
      const analysisData = await getAnalysis(id, analysisId);
      setAnalysis(analysisData);
      return analysisData;
    } catch (error) {
      throw error;
    }
  }, [id, analysisId]);

  /**
   * Load data on mount.
   */
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      setError(null);

      try {
        const [rfpResponse, analysisData] = await Promise.all([
          get(`/rfps/${id}`),
          fetchAnalysis(),
        ]);

        setRfp(rfpResponse.data.rfp);
        setAnalysis(analysisData);
      } catch (err) {
        if (err.response?.status === 404) {
          setError('Analysis not found');
        } else if (err.response?.status === 403) {
          setError('You do not have permission to view this analysis');
        } else {
          setError('Failed to load analysis data');
        }
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [id, analysisId, fetchAnalysis]);

  /**
   * Poll for updates while processing.
   */
  useEffect(() => {
    if (!analysis) return;
    if (analysis.status !== 'pending' && analysis.status !== 'processing') return;

    const interval = setInterval(async () => {
      try {
        const updated = await fetchAnalysis();
        if (updated.status === 'completed' || updated.status === 'failed') {
          clearInterval(interval);
        }
      } catch {
        clearInterval(interval);
      }
    }, POLL_INTERVAL);

    return () => clearInterval(interval);
  }, [analysis, fetchAnalysis]);

  /**
   * Sort parameters.
   */
  const sortedParameters = analysis?.technicalParameters
    ? [...analysis.technicalParameters].sort((a, b) => {
        let aVal = a[sortField];
        let bVal = b[sortField];

        // Handle pages array
        if (sortField === 'pages') {
          aVal = (a.pages || []).length;
          bVal = (b.pages || []).length;
        }

        if (typeof aVal === 'string') aVal = aVal.toLowerCase();
        if (typeof bVal === 'string') bVal = bVal.toLowerCase();

        if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
        return 0;
      })
    : [];

  /**
   * Handle sort.
   */
  const handleSort = (field) => {
    if (field === sortField) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  /**
   * Render sort indicator.
   */
  const SortIndicator = ({ field }) => {
    if (sortField !== field) return null;
    return <span className="ml-1">{sortDir === 'asc' ? '\u2191' : '\u2193'}</span>;
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin mx-auto" />
          <p className="mt-4 text-accent-500">Loading analysis...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error && !analysis) {
    return (
      <div className="space-y-6">
        <div className="bg-error-light border-l-4 border-error p-4 rounded">
          <p className="text-error-dark">{error}</p>
        </div>
        <Link to={`/app/rfps/${id}`} className="btn-outline">
          Back to RFP
        </Link>
      </div>
    );
  }

  // Processing state
  if (analysis?.status === 'pending' || analysis?.status === 'processing') {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div>
          <div className="flex items-center gap-3 mb-2">
            <Link to="/app/rfps" className="text-accent-500 hover:text-accent-700">
              RFPs
            </Link>
            <span className="text-accent-400">/</span>
            <Link to={`/app/rfps/${id}`} className="text-accent-500 hover:text-accent-700">
              {rfp?.rfpNumber}
            </Link>
            <span className="text-accent-400">/</span>
            <span className="text-accent-800">Analysis</span>
          </div>
          <h1 className="text-2xl font-bold text-accent-800">{rfp?.title}</h1>
        </div>

        {/* Processing Card */}
        <div className="card">
          <div className="card-body text-center py-12">
            <div className="w-16 h-16 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin mx-auto" />
            <h2 className="mt-6 text-xl font-semibold text-accent-800">
              {analysis.status === 'pending' ? 'Starting Analysis...' : 'Analyzing RFP...'}
            </h2>
            <p className="mt-2 text-accent-500">
              This may take a few minutes depending on document size.
            </p>
            <div className="mt-6 space-y-2 text-sm text-accent-500">
              <p>Extracting text from PDF...</p>
              <p>Identifying keywords and specifications...</p>
              <p>Generating summaries...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Failed state
  if (analysis?.status === 'failed') {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div>
          <div className="flex items-center gap-3 mb-2">
            <Link to="/app/rfps" className="text-accent-500 hover:text-accent-700">
              RFPs
            </Link>
            <span className="text-accent-400">/</span>
            <Link to={`/app/rfps/${id}`} className="text-accent-500 hover:text-accent-700">
              {rfp?.rfpNumber}
            </Link>
            <span className="text-accent-400">/</span>
            <span className="text-accent-800">Analysis</span>
          </div>
          <h1 className="text-2xl font-bold text-accent-800">{rfp?.title}</h1>
        </div>

        {/* Error Card */}
        <div className="card border-error">
          <div className="card-body">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0">
                <svg
                  className="w-10 h-10 text-error"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-error-dark">Analysis Failed</h3>
                <p className="mt-1 text-accent-600">
                  {analysis.errorMessage || 'An error occurred during analysis.'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-4">
          <Link to={`/app/rfps/${id}/analyze`} className="btn-primary">
            Try Again
          </Link>
          <Link to={`/app/rfps/${id}`} className="btn-outline">
            Back to RFP
          </Link>
        </div>
      </div>
    );
  }

  // Completed state - render results
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <Link to="/app/rfps" className="text-accent-500 hover:text-accent-700">
              RFPs
            </Link>
            <span className="text-accent-400">/</span>
            <Link to={`/app/rfps/${id}`} className="text-accent-500 hover:text-accent-700">
              {rfp?.rfpNumber}
            </Link>
            <span className="text-accent-400">/</span>
            <span className="text-accent-800">Analysis</span>
          </div>
          <h1 className="text-2xl font-bold text-accent-800">{rfp?.title}</h1>
          <p className="mt-1 text-accent-500">
            Analyzed {new Date(analysis.completedAt).toLocaleDateString()} -{' '}
            {analysis.extendedMode ? 'Extended Analysis' : 'Normal Analysis'}
          </p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={handleDownloadPdf}
            disabled={downloadingPdf}
            className="btn-primary flex items-center gap-2"
          >
            {downloadingPdf ? (
              <>
                <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                Downloading...
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
                Download PDF
              </>
            )}
          </button>
          <button
            onClick={handleDownloadCsv}
            disabled={downloadingCsv}
            className="btn-outline flex items-center gap-2"
          >
            {downloadingCsv ? (
              <>
                <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                Downloading...
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
                Download CSV
              </>
            )}
          </button>
          <button
            onClick={handleDownloadSchema}
            disabled={downloadingSchema}
            className="btn-outline flex items-center gap-2"
            title="Download the JSON Schema that defines the structured output format"
          >
            {downloadingSchema ? (
              <>
                <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                Downloading...
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                  />
                </svg>
                Schema
              </>
            )}
          </button>
          <Link to={`/app/rfps/${id}/analyze`} className="btn-outline">
            New Analysis
          </Link>
          <Link to={`/app/rfps/${id}`} className="btn-outline">
            Back to RFP
          </Link>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-accent-200">
        <nav className="flex gap-8">
          {[
            { id: 'summary', label: 'Summary' },
            { id: 'parameters', label: 'Parameters' },
            { id: 'chat', label: 'Ask Questions' },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === tab.id
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-accent-500 hover:text-accent-700 hover:border-accent-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'summary' && (
        <div className="space-y-6">
          {/* Project Summary */}
          <div className="card">
            <div className="card-header">
              <h2 className="text-lg font-semibold text-accent-800">Project Summary</h2>
            </div>
            <div className="card-body">
              <p className="text-accent-700 whitespace-pre-wrap">
                {analysis.projectSummary || 'No summary available.'}
              </p>
            </div>
          </div>

          {/* Technical Abstract */}
          <div className="card">
            <div className="card-header">
              <h2 className="text-lg font-semibold text-accent-800">Technical Abstract</h2>
            </div>
            <div className="card-body">
              <p className="text-accent-700 whitespace-pre-wrap">
                {analysis.technicalAbstract || 'No technical abstract available.'}
              </p>
            </div>
          </div>

          {/* Metrics */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="card">
              <div className="card-body text-center">
                <p className="text-2xl font-bold text-primary-600">
                  {analysis.technicalParameters?.length || 0}
                </p>
                <p className="text-sm text-accent-500">Parameters</p>
              </div>
            </div>
            <div className="card">
              <div className="card-body text-center">
                <p className="text-2xl font-bold text-success">
                  {analysis.technicalParameters?.filter((p) => p.value !== 'Not Found').length || 0}
                </p>
                <p className="text-sm text-accent-500">Found</p>
              </div>
            </div>
            <div className="card">
              <div className="card-body text-center">
                <p className="text-2xl font-bold text-accent-600">
                  {analysis.tokenCount ? analysis.tokenCount.toLocaleString() : 'N/A'}
                </p>
                <p className="text-sm text-accent-500">Tokens</p>
              </div>
            </div>
            <div className="card">
              <div className="card-body text-center">
                <p className="text-2xl font-bold text-accent-600">
                  {analysis.processingTimeMs
                    ? `${(analysis.processingTimeMs / 1000).toFixed(1)}s`
                    : 'N/A'}
                </p>
                <p className="text-sm text-accent-500">Processing</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'parameters' && (
        <div className="card">
          <div className="card-header">
            <h2 className="text-lg font-semibold text-accent-800">Technical Parameters</h2>
            <p className="text-sm text-accent-500 mt-1">Click column headers to sort</p>
          </div>
          <div className="overflow-x-auto">
            <table
              className="min-w-full divide-y divide-accent-200"
              style={{ tableLayout: 'fixed' }}
            >
              <thead className="bg-accent-50">
                <tr>
                  <th
                    onClick={() => handleSort('parameter')}
                    style={{ width: '15%' }}
                    className="px-6 py-3 text-left text-xs font-medium text-accent-500 uppercase tracking-wider cursor-pointer hover:bg-accent-100"
                  >
                    Parameter
                    <SortIndicator field="parameter" />
                  </th>
                  <th
                    onClick={() => handleSort('value')}
                    style={{ width: '75%' }}
                    className="px-6 py-3 text-left text-xs font-medium text-accent-500 uppercase tracking-wider cursor-pointer hover:bg-accent-100"
                  >
                    Value
                    <SortIndicator field="value" />
                  </th>
                  <th
                    onClick={() => handleSort('pages')}
                    style={{ width: '10%' }}
                    className="px-6 py-3 text-left text-xs font-medium text-accent-500 uppercase tracking-wider cursor-pointer hover:bg-accent-100"
                  >
                    Pages
                    <SortIndicator field="pages" />
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-accent-200">
                {sortedParameters.map((param, index) => (
                  <tr key={index} className={param.value === 'Not Found' ? 'bg-accent-50' : ''}>
                    <td
                      className="px-6 py-4 text-sm font-medium text-accent-900 break-words"
                      style={{ width: '15%' }}
                    >
                      {param.parameter}
                    </td>
                    <td className="px-6 py-4 text-sm text-accent-700" style={{ width: '75%' }}>
                      <span className={param.value === 'Not Found' ? 'text-accent-400 italic' : ''}>
                        {param.value}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-accent-500" style={{ width: '10%' }}>
                      {param.pages && param.pages.length > 0 ? param.pages.join(', ') : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'chat' && (
        <div className="card">
          <div className="card-header">
            <h2 className="text-lg font-semibold text-accent-800">Ask Questions</h2>
            <p className="text-sm text-accent-500 mt-1">
              Ask follow-up questions about this RFP analysis.
            </p>
          </div>
          <div className="card-body">
            <AnalysisChat rfpId={id} analysisId={analysisId} />
          </div>
        </div>
      )}
    </div>
  );
}

export default RFPAnalysisResults;
